import { ADDRESS_TYPE, ROLE_TYPE } from '@/api/type/commonTypes';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

interface AddressSectionProps {
  countryCode: string;
  address: ADDRESS_TYPE;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  role: ROLE_TYPE;
}
const AddressSection = ({
  countryCode,
  address,
  onChange,
  role,
}: AddressSectionProps) => {
  const { t } = useTranslation();
  return (
    <FormGroup>
      <Label $required>
        {role === 'CENTER'
          ? t('playerSignUp.centerAddress')
          : t('playerSignUp.officeAddress')}
      </Label>
      {countryCode === 'KR' ? (
        // 대한민국 주소 양식 (3칸)
        <AddressWrap>
          <Row>
            <PostalCodeInput
              type="text"
              name="postalCode"
              value={address.postalCode}
              onChange={onChange}
              placeholder={t('common.postcode')}
              required
            />
          </Row>
          <Input
            type="text"
            name="address1"
            value={address.address1}
            onChange={onChange}
            placeholder={t('common.address')}
            required
          />
          <Input
            type="text"
            name="address2"
            value={address.address2}
            onChange={onChange}
            placeholder={t('playerSignUp.detailAddressPlaceholder')}
            required
          />
        </AddressWrap>
      ) : (
        // 해외 주소 양식 (5칸)
        <AddressWrap>
          <Input
            type="text"
            name="address1"
            value={address.address1}
            onChange={onChange}
            placeholder="Address Line 1"
            required
          />
          <Input
            type="text"
            name="address2"
            value={address.address2}
            onChange={onChange}
            placeholder="Address Line 2 (Optional)"
          />
          <AddressGrid>
            <Input
              type="text"
              name="city"
              value={address.city}
              onChange={onChange}
              placeholder="City"
              required
            />
            <Input
              type="text"
              name="state"
              value={address.state}
              onChange={onChange}
              placeholder="State/Region"
              required
            />
            <Input
              type="text"
              name="postalCode"
              value={address.postalCode}
              onChange={onChange}
              placeholder="Zip Code"
              required
            />
          </AddressGrid>
        </AddressWrap>
      )}
    </FormGroup>
  );
};

export default AddressSection;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

interface LabelProps {
  $required?: boolean;
}

const Label = styled.label<LabelProps>`
  font-size: 13px;
  font-weight: 600;
  color: #374151;

  &::after {
    content: ${(props) => (props.$required ? "' *'" : "''")};
    color: #ef4444;
  }
`;

const Row = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

interface InputProps {
  $error?: boolean;
}

const Input = styled.input<InputProps>`
  width: 100%;
  height: 40px;
  padding: 0 12px;
  font-size: 14px;
  color: #374151;

  border: 1px solid ${({ $error }) => ($error ? '#ef4444' : '#d1d5db')};
  border-radius: 6px;
  background: #fff;

  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: #2563eb;
  }

  &:disabled,
  &:read-only {
    background: #f9fafb;
    color: #6b7280;
  }
`;

const PostalCodeInput = styled(Input)`
  width: 120px;
`;

const AddressGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
`;

const AddressWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
